import type { ReactNode } from 'react'
import { cn } from './cn'
import { t } from '@/lib/i18n'

function clamp(value: number) {
  return Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0))
}

export interface ProgressBarProps {
  /** 0 to 100. */
  value: number
  /** Text inside the bar: omitted shows "45%", true shows "45% complete", a node replaces the text. */
  label?: boolean | ReactNode
  className?: string
  'aria-label'?: string
}

/**
 * Skool style progress pill (REDESIGN sections 4 and 9): gray track, height 18, MG cyan fill (the progress
 * token, never the gold CTA) and the percentage
 * inside at the left (12/600). Over the fill the text switches to navy through a clipped copy drawn with CSS
 * content, so the words appear once in the DOM.
 */
export function ProgressBar({ value, label, className, 'aria-label': ariaLabel }: ProgressBarProps) {
  const pct = Math.round(clamp(value))
  const text = label === true ? `${pct}% complete` : label === undefined || label === false || label === null ? `${pct}%` : label
  const textClass = 'flex h-full items-center truncate px-2 text-[12px] leading-none font-semibold tabular-nums'
  return (
    <div className={cn('flex items-center', className)}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={ariaLabel ?? (typeof text === 'string' ? undefined : 'Progress')}
        aria-valuetext={`${pct}% complete`}
        className="relative h-[18px] min-w-0 flex-1 overflow-hidden rounded-full bg-track"
      >
        <div className="absolute inset-y-0 left-0 rounded-full bg-progress transition-[width] duration-500 ease-out" style={{ width: `${pct}%` }} />
        <span className={cn('relative text-text', textClass)}>{text}</span>
        {typeof text === 'string' && pct > 0 ? (
          <span
            aria-hidden="true"
            data-label={text}
            className={cn('pointer-events-none absolute inset-0 text-on-progress before:content-[attr(data-label)]', textClass)}
            style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
          />
        ) : null}
      </div>
    </div>
  )
}

export interface ProgressRingProps {
  value: number
  /** Outer size in px (default 140). */
  size?: number
  /** Stroke width (default 6). */
  stroke?: number
  className?: string
  /** Centered content, usually an Avatar. */
  children?: ReactNode
  'aria-label'?: string
}

/** SVG ring with a solid stroke in the level blue and round caps, used around big avatars. */
export function ProgressRing({ value, size = 140, stroke = 6, className, children, 'aria-label': ariaLabel }: ProgressRingProps) {
  const pct = clamp(value)
  const r = (size - stroke) / 2
  const c = 2 * Math.PI * r
  return (
    <div
      className={cn('relative inline-flex shrink-0 items-center justify-center', className)}
      style={{ width: size, height: size }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={ariaLabel ?? t('Progress')}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 -rotate-90" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-track)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-level)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct / 100)}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      {children && <div className="relative">{children}</div>}
    </div>
  )
}
